import React, { useState, useCallback, useEffect } from 'react';
import {
  Mail, Download, Copy, CheckCircle, AlertCircle, Clock,
  Sparkles, User, Euro, Loader2
} from 'lucide-react';
import { useWebSocket } from '../../hooks/useWebSocket';
import websocketManager from '../../services/websocketManager';
import api from '../../services/api';
import { Modal, Alert } from '../shared';

const EmailGenerationModal = ({ needId, candidates, candidateDetails, open, onClose, onSuccess, onError }) => {
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [activeTab, setActiveTab] = useState('cv_mail'); // 'cv_mail' or 'need_mail'
  const [candidateData, setCandidateData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [mailJobId, setMailJobId] = useState(null);
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [emailGenerationStatus, setEmailGenerationStatus] = useState('idle'); // 'idle', 'generating', 'completed', 'error'
  const [processedMessages, setProcessedMessages] = useState(new Set()); // ✅ ADD: Message deduplication
  // Need mail specific state
  const [needMailJobId, setNeedMailJobId] = useState(null);
  const [needMailGenerationStatus, setNeedMailGenerationStatus] = useState('idle');
  const [copyFeedback, setCopyFeedback] = useState(false);

  const fetchGeneratedEmail = async (jobId) => {
    try {
      const response = await api.get(`/analyses-ia/mails/${jobId}`);
      setGeneratedEmail(response.data.email_content || response.data);
      setLoading(false);
      setEmailGenerationStatus('completed');
    } catch (error) {
      console.error('Error fetching generated email:', error);
      setLoading(false);
      setEmailGenerationStatus('error');
      onError('Failed to retrieve generated email');
    }
  };

  const fetchGeneratedNeedEmail = async (jobId) => {
    try {
      const response = await api.get(`/analyses-ia/need-mails/${jobId}`);
      setGeneratedEmail(response.data.email_content || response.data);
      setLoading(false);
      setNeedMailGenerationStatus('completed');
    } catch (error) {
      console.error('Error fetching generated need email:', error);
      setLoading(false);
      setNeedMailGenerationStatus('error');
      onError('Failed to retrieve generated need email');
    }
  };

  // ✅ FIX: Enhanced WebSocket message handler with correct data access
  const handleWebSocketMessage = useCallback((message) => {
    if (!message) return;

    console.log('📧 EmailModal - WebSocket message received:', message);

    // Accept both new (`event_type`) and legacy (`type`) fields
    const messageType = message.event_type || message.type;

    // Handle both mail generation and need description mail messages
    if (!messageType || (!messageType.includes('mail_generation') && !messageType.includes('need_description_mail'))) {
      return;
    }

    // Check if this message concerns the current job
    const messageJobId = message.entity_id || message.job_id;
    const isForOurJob = messageJobId === mailJobId || messageJobId === needMailJobId;
    
    console.log('📧 EmailModal - Processing mail generation message:', {
      messageType,
      messageJobId,
      currentMailJobId: mailJobId,
      currentNeedMailJobId: needMailJobId,
      isForOurJob,
      data: message.data, // ✅ FIX: Log the actual data field
      payload: message.payload
    });

    // Handle CV mail generation messages
    if (messageType === 'mail_generation_complete' && messageJobId === mailJobId) {
      if (!mailJobId && messageJobId) {
        console.log('📧 EmailModal - Setting mailJobId from complete message:', messageJobId);
        setMailJobId(messageJobId);
      }

      if (isForOurJob || !mailJobId) {
        console.log('📧 EmailModal - Mail generation completed, fetching email...');
        setLoading(false);
        setEmailGenerationStatus('completed');
        fetchGeneratedEmail(messageJobId);
      }

    } else if (messageType === 'mail_generation_progress' && messageJobId === mailJobId) {
      if (!mailJobId && messageJobId) {
        console.log('📧 EmailModal - Setting mailJobId from progress message:', messageJobId);
        setMailJobId(messageJobId);
      }

      if (isForOurJob || !mailJobId) {
        // ✅ FIX: Use message.data instead of message.payload
        console.log('📧 EmailModal - Mail generation progress:', message.data);
        if (message.data?.progress) {
          // You could add a progress bar here if desired
        }
        
        // ✅ ADD: Check if this is actually a completion disguised as progress
        if (message.status === 'completed' || message.data?.status === 'completed') {
          console.log('📧 EmailModal - Progress message indicates completion, fetching email...');
          setLoading(false);
          setEmailGenerationStatus('completed');
          fetchGeneratedEmail(messageJobId);
        }
      }

    } else if (messageType === 'mail_generation_error' && messageJobId === mailJobId) {
      if (!mailJobId && messageJobId) {
        console.log('📧 EmailModal - Setting mailJobId from error message:', messageJobId);
        setMailJobId(messageJobId);
      }

      if (isForOurJob || !mailJobId) {
        // ✅ FIX: Use message.data instead of message.payload
        console.log('📧 EmailModal - Mail generation error:', message.data);
        setLoading(false);
        setEmailGenerationStatus('error');
        onError(message.data?.error || 'Email generation failed');
      }
    }
    // Handle need description mail messages
    else if (messageType === 'need_description_mail_complete' && messageJobId === needMailJobId) {
      if (!needMailJobId && messageJobId) {
        console.log('📧 EmailModal - Setting needMailJobId from complete message:', messageJobId);
        setNeedMailJobId(messageJobId);
      }

      if (isForOurJob || !needMailJobId) {
        console.log('📧 EmailModal - Need mail generation completed, fetching email...');
        setLoading(false);
        setNeedMailGenerationStatus('completed');
        fetchGeneratedNeedEmail(messageJobId);
      }

    } else if (messageType === 'need_description_mail_progress' && messageJobId === needMailJobId) {
      if (!needMailJobId && messageJobId) {
        console.log('📧 EmailModal - Setting needMailJobId from progress message:', messageJobId);
        setNeedMailJobId(messageJobId);
      }

      if (isForOurJob || !needMailJobId) {
        console.log('📧 EmailModal - Need mail generation progress:', message.data);
        if (message.data?.progress) {
          // You could add a progress bar here if desired
        }
        
        if (message.status === 'completed' || message.data?.status === 'completed') {
          console.log('📧 EmailModal - Need mail progress message indicates completion, fetching email...');
          setLoading(false);
          setNeedMailGenerationStatus('completed');
          fetchGeneratedNeedEmail(messageJobId);
        }
      }

    } else if (messageType === 'need_description_mail_error' && messageJobId === needMailJobId) {
      if (!needMailJobId && messageJobId) {
        console.log('📧 EmailModal - Setting needMailJobId from error message:', messageJobId);
        setNeedMailJobId(messageJobId);
      }

      if (isForOurJob || !needMailJobId) {
        console.log('📧 EmailModal - Need mail generation error:', message.data);
        setLoading(false);
        setNeedMailGenerationStatus('error');
        onError(message.data?.error || 'Need description email generation failed');
      }
    }
  }, [mailJobId, needMailJobId, onError, fetchGeneratedEmail, fetchGeneratedNeedEmail]);
  
  // ✅ FIX: Use only one subscription method to avoid duplicates
  useWebSocket(
    localStorage.getItem('access_token'),
    handleWebSocketMessage,
    activeTab === 'cv_mail' ? mailJobId : needMailJobId // Subscribe to specific job based on active tab
  );

  // ✅ ADD: Timeout fallback in case completion message is missed
  useEffect(() => {
    if (mailJobId && emailGenerationStatus === 'generating' && activeTab === 'cv_mail') {
      const timeout = setTimeout(() => {
        console.log('📧 EmailModal - Timeout reached, checking for completed email...');
        fetchGeneratedEmail(mailJobId);
      }, 120000); // 2 minutes timeout

      return () => clearTimeout(timeout);
    }
  }, [mailJobId, emailGenerationStatus, activeTab, fetchGeneratedEmail]);

  // ✅ ADD: Polling fallback to check for completion
  useEffect(() => {
    if (mailJobId && emailGenerationStatus === 'generating' && activeTab === 'cv_mail') {
      const pollInterval = setInterval(async () => {
        console.log('📧 EmailModal - Polling for completion...');
        try {
          const response = await fetch(`/api/v1/analyses-ia/mails/${mailJobId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          
          if (response.ok) {
            console.log('📧 EmailModal - Email found via polling, marking as completed');
            const data = await response.json();
            setGeneratedEmail(data.email_content || data);
            setLoading(false);
            setEmailGenerationStatus('completed');
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.log('📧 EmailModal - Polling check failed (email not ready yet):', error.message);
        }
      }, 5000); // Poll every 5 seconds

      // Cleanup interval
      return () => clearInterval(pollInterval);
    }
  }, [mailJobId, emailGenerationStatus, activeTab]);

  const handleCandidateToggle = (candidateId) => {
    const newSelected = new Set(selectedCandidates);
    if (newSelected.has(candidateId)) {
      newSelected.delete(candidateId);
      const newData = { ...candidateData };
      delete newData[candidateId];
      setCandidateData(newData);
    } else {
      newSelected.add(candidateId);
      setCandidateData(prev => ({
        ...prev,
        [candidateId]: {
          tjm: '',
          availability: ''
        }
      }));
    }
    setSelectedCandidates(newSelected);
    setErrors({});
  };

  const handleCandidateDataChange = (candidateId, field, value) => {
    setCandidateData(prev => ({
      ...prev,
      [candidateId]: {
        ...prev[candidateId],
        [field]: value
      }
    }));
    
    if (errors[candidateId]?.[field]) {
      setErrors(prev => ({
        ...prev,
        [candidateId]: {
          ...prev[candidateId],
          [field]: null
        }
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let hasErrors = false;

    if (selectedCandidates.size === 0) {
      setErrors({ general: 'Please select at least one candidate' });
      return false;
    }

    selectedCandidates.forEach(candidateId => {
      const data = candidateData[candidateId];
      if (!data?.tjm?.trim()) {
        newErrors[candidateId] = { ...newErrors[candidateId], tjm: 'TJM is required' };
        hasErrors = true;
      }
      if (!data?.availability?.trim()) {
        newErrors[candidateId] = { ...newErrors[candidateId], availability: 'Availability is required' };
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});
    setEmailGenerationStatus('generating');
    setGeneratedEmail('');
    setProcessedMessages(new Set()); // ✅ FIX: Clear processed messages for new job

    try {
      const jobAnalysisKey = `needs/${needId}/jd_analysis.json`;
      const cvAnalysisKeys = Array.from(selectedCandidates).map(candidateId => 
        `candidates/${candidateId}/cv_analysis.json`
      );
      
      const cvDetails = {};
      selectedCandidates.forEach(candidateId => {
        cvDetails[candidateId] = {
          tjm: candidateData[candidateId].tjm,
          availability: candidateData[candidateId].availability
        };
      });

      console.log('📧 EmailModal - Submitting email generation request...');

      // ✅ FIX: Ensure WebSocket connection is established before making the request
      const token = localStorage.getItem('access_token');
      if (token) {
        websocketManager.connect(token);
        // Give WebSocket a moment to connect
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // ✅ FIX: Use the configured api instance instead of direct fetch to ensure token refresh
      const response = await api.post('/analyses-ia/email-generations', {
        job_analysis_key: jobAnalysisKey,
        cv_analysis_keys: cvAnalysisKeys,
        cv_details: cvDetails
      });

      const result = response.data;
      console.log('📧 EmailModal - Email generation job started:', result.mail_job_id);
      setMailJobId(result.mail_job_id);

      // ✅ FIX: Immediately subscribe to the job after getting the job ID
      if (result.mail_job_id) {
        websocketManager.subscribeToJob(result.mail_job_id, handleWebSocketMessage);
        setNeedMailJobId(null); // Clear need mail job ID when switching to CV mail
      }

    } catch (error) {
      console.error('❌ Error generating email:', error);
      setLoading(false);
      setEmailGenerationStatus('error');
      onError(error.message);
    }
  };

  const handleNeedMailSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setErrors({});
    setNeedMailGenerationStatus('generating');
    setGeneratedEmail('');
    setEmailGenerationStatus('idle');
    setProcessedMessages(new Set());

    try {
      const jobAnalysisKey = `needs/${needId}/jd_analysis.json`;
      
      console.log('📧 EmailModal - Submitting need description email generation request...');

      // ✅ FIX: Ensure WebSocket connection is established before making the request
      const token = localStorage.getItem('access_token');
      if (token) {
        websocketManager.connect(token);
        // Give WebSocket a moment to connect
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // ✅ FIX: Use the configured api instance instead of direct fetch to ensure token refresh
      const response = await api.post('/analyses-ia/need-email-generations', {
        job_analysis_key: jobAnalysisKey
      });

      const result = response.data;
      console.log('📧 EmailModal - Need description email generation job started:', result.need_mail_job_id);
      setNeedMailJobId(result.need_mail_job_id);
      setMailJobId(null); // Clear CV mail job ID when switching to need mail

    } catch (error) {
      console.error('❌ Error generating need description email:', error);
      setLoading(false);
      setNeedMailGenerationStatus('error');
      onError(error.message);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(generatedEmail);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2000);
  };

  const handleDownloadEmail = () => {
    const blob = new Blob([generatedEmail], { type: 'text/plain' });
    const jobId = activeTab === 'cv_mail' ? mailJobId : needMailJobId;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recruitment_email_${jobId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setSelectedCandidates(new Set());
    setCandidateData({});
    setErrors({});
    setMailJobId(null);
    setNeedMailJobId(null);
    setNeedMailGenerationStatus('idle');
    setGeneratedEmail('');
    setEmailGenerationStatus('idle');
    setLoading(false);
    setProcessedMessages(new Set()); // ✅ FIX: Clear processed messages on reset
  };

  // Reset form when modal opens to ensure clean state
  useEffect(() => {
    if (open) {
      console.log('📧 EmailModal - Modal opened, resetting form');
      resetForm();
    }
  }, [open]);

  const handleClose = () => {
    if (!loading) {
      console.log('📧 EmailModal - Closing modal');
      resetForm();
      onClose();
    }
  };

  const getCurrentStatus = () => {
    if (activeTab === 'cv_mail') {
      return emailGenerationStatus;
    } else {
      return needMailGenerationStatus;
    }
  };

  const getCurrentJobId = () => {
    if (activeTab === 'cv_mail') {
      return mailJobId;
    } else {
      return needMailJobId;
    }
  };

  const getSubtitle = () => {
    if (getCurrentStatus() === 'completed') return 'Email generated successfully!';
    if (getCurrentStatus() === 'generating') return 'AI is crafting your perfect email...';
    return 'Select candidates and provide details for AI email generation';
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create Perfect Mail with IA"
      subtitle={getSubtitle()}
      icon={Sparkles}
      maxWidth="max-w-6xl"
      headerGradient="from-purple-50 to-pink-50"
    >
          {getCurrentStatus() === 'generating' ? (
            // Loading State
            <div className="p-8 text-center">
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <Sparkles className="animate-pulse text-purple-600" size={48} />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Generating Perfect Email with AI...
                  </p>
                  <p className="text-sm text-gray-600">
                    This may take a few minutes. Please wait while our AI crafts the perfect recruitment email.
                  </p>
                  {getCurrentJobId() && (
                    <p className="text-xs text-gray-500 mt-2">
                      Job ID: {getCurrentJobId()}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  <span className="text-sm text-gray-500">Processing...</span>
                </div>
              </div>
            </div>
          ) : getCurrentStatus() === 'completed' && generatedEmail ? (
            // Email Result Display
            <div className="p-6 space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                <CheckCircle size={16} className="text-green-600 mr-2" />
                <p className="text-green-800 text-sm">
                  <strong>Success!</strong> Your perfect recruitment email has been generated.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Generated Email
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCopyEmail}
                      className={`flex items-center px-3 py-2 rounded-lg transition-colors text-sm text-white ${copyFeedback ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                      {copyFeedback ? <CheckCircle size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                      {copyFeedback ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={handleDownloadEmail}
                      className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <Download size={14} className="mr-1" />
                      Download
                    </button>
                  </div>
                </div>

                <div className="border border-gray-300 rounded-lg">
                  <textarea
                    value={generatedEmail}
                    onChange={(e) => setGeneratedEmail(e.target.value)}
                    className="w-full h-96 p-4 border-0 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Generated email will appear here..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleClose}
                  className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    // Reset to form state to generate another email
                    setEmailGenerationStatus('idle');
                    setGeneratedEmail('');
                    setMailJobId(null);
                  }}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
                >
                  <Sparkles className="mr-2" size={16} />
                  Generate Another Email
                </button>
              </div>
            </div>
          ) : getCurrentStatus() === 'error' ? (
            // Error State
            <div className="p-8 text-center">
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <AlertCircle className="text-red-600" size={48} />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Email Generation Failed
                  </p>
                  <p className="text-sm text-gray-600">
                    There was an error generating your email. Please try again.
                  </p>
                  {getCurrentJobId() && (
                    <p className="text-xs text-gray-500 mt-2">
                      Job ID: {getCurrentJobId()}
                    </p>
                  )}
                </div>
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => {
                      setEmailGenerationStatus('idle');
                      setMailJobId(null);
                    }}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleClose}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // Form State
            <>
            {/* Tabs */}
            <div className="p-6">
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('cv_mail')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'cv_mail'
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Mail className="w-4 h-4 inline mr-2" />
                    CV Mail Generation
                  </button>
                  <button
                    onClick={() => setActiveTab('need_mail')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'need_mail'
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    Need Description Mail
                  </button>
                </nav>
              </div>

              {activeTab === 'cv_mail' ? (
                <form onSubmit={handleSubmit} className="space-y-6">
              {/* General Error */}
              {errors.general && (
                <Alert variant="error">{errors.general}</Alert>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <strong>How it works:</strong> Select the candidates you want to include in the recruitment email. 
                    For each candidate, provide their daily rate (TJM) and availability. Our AI will generate a 
                    personalized recruitment email highlighting the best matches.
                  </div>
                </div>
              </div>

              {/* Candidates Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Select Candidates ({candidates.length} available)
                </h3>

                {candidates.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p>No candidates available for this need</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {candidates.map((candidate) => {
                      const candidateDetail = candidateDetails[candidate.id] || candidate;
                      const isSelected = selectedCandidates.has(candidate.id);
                      
                      return (
                        <div key={candidate.id} className={`border rounded-lg p-4 transition-all ${
                          isSelected ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          {/* Candidate Header */}
                          <div className="flex items-center gap-3 mb-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleCandidateToggle(candidate.id)}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{candidateDetail.name}</div>
                              <div className="text-sm text-gray-500">{candidateDetail.email}</div>
                            </div>
                            {isSelected && (
                              <CheckCircle className="w-5 h-5 text-purple-600" />
                            )}
                          </div>

                          {/* Candidate Details Form (only show if selected) */}
                          {isSelected && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-purple-200">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  <Euro className="w-4 h-4 inline mr-1" />
                                  TJM (Daily Rate) *
                                </label>
                                <input
                                  type="text"
                                  value={candidateData[candidate.id]?.tjm || ''}
                                  onChange={(e) => handleCandidateDataChange(candidate.id, 'tjm', e.target.value)}
                                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                                    errors[candidate.id]?.tjm ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                  }`}
                                  placeholder="e.g., 500€, 600€/jour"
                                  disabled={loading}
                                />
                                {errors[candidate.id]?.tjm && (
                                  <p className="mt-1 text-sm text-red-600">{errors[candidate.id].tjm}</p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  <Clock className="w-4 h-4 inline mr-1" />
                                  Availability *
                                </label>
                                <input
                                  type="text"
                                  value={candidateData[candidate.id]?.availability || ''}
                                  onChange={(e) => handleCandidateDataChange(candidate.id, 'availability', e.target.value)}
                                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                                    errors[candidate.id]?.availability ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                  }`}
                                  placeholder="e.g., Immédiate, 2 semaines, 1 mois"
                                  disabled={loading}
                                />
                                {errors[candidate.id]?.availability && (
                                  <p className="mt-1 text-sm text-red-600">{errors[candidate.id].availability}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Summary */}
              {selectedCandidates.size > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">
                      {selectedCandidates.size} candidate{selectedCandidates.size > 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <p className="text-sm text-green-700">
                    AI will generate a personalized recruitment email for the selected candidate{selectedCandidates.size > 1 ? 's' : ''}.
                  </p>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedCandidates.size === 0}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Generating Email...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2" size={16} />
                      Generate Perfect Mail
                    </>
                  )}
                </button>
              </div>
                </form>
              ) : (
                // Need Description Mail Tab
                <form onSubmit={handleNeedMailSubmit} className="space-y-6">
                  {/* Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <strong>How it works:</strong> Generate a professional email describing only the job requirement/need. 
                        This email will focus on presenting the opportunity to potential candidates without including specific candidate profiles.
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">
                        Need Description Email
                      </span>
                    </div>
                    <p className="text-sm text-green-700">
                      AI will generate a professional email describing the job opportunity and requirements.
                    </p>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={loading}
                      className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Generating Need Description Email...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2" size={16} />
                          Generate Need Description Email
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
            </>
          )}
    </Modal>
  );
};

export default EmailGenerationModal;